<script setup>
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";
import { DateTime, Settings } from 'luxon';
import { PrimaryButton, SecondaryButton, DateRangePicker, useSnackbarStore } from '@es-bill/es-bill-common-components';
import { useCommunitiesStore } from "@/stores/communitiesStore";
import { fetchSumsReport, buildSumsReport, fmtKwh } from "@/services/accountabilityService";

const $log = inject('log');
const { t } = useI18n();
Settings.defaultZone = 'Europe/Vienna';

const communitiesStore = useCommunitiesStore();
const snackbarStore = useSnackbarStore();

const communityAvailable = computed(() => communitiesStore.allCommunities.map(c => ({
  title: c.name,
  props: { subtitle: c.ecid },
  ecn: c.ecn,
  ecid: c.ecid,
})));

const selectedCommunity = ref(null);
const dateRange = ref([]);
const loading = ref(false);
const report = ref(null);

const isoRange = computed(() => {
  if (!dateRange.value || dateRange.value.length === 0) return null;
  const days = dateRange.value
    .map(d => DateTime.fromJSDate(new Date(d)).toISODate())
    .sort();
  return { from: days[0], to: days[days.length - 1] };
});

const requestReady = computed(() => selectedCommunity.value && isoRange.value);
const reportText = computed(() => report.value ? buildSumsReport(report.value, selectedCommunity.value?.title) : '');

const dsoHeader = computed(() => [
  { title: t('sumsReport.dso'), key: 'ecnDso' },
  { title: t('sumsReport.meterCode'), key: 'meterCode' },
  { title: 'kWh', key: 'totalEnergy', value: item => fmtKwh(item.totalEnergy) },
  { title: t('sumsReport.meteringMethod'), key: 'meteringMethod' },
  { title: t('sumsReport.documents'), key: 'documentCount' },
]);

async function loadReport() {
  loading.value = true;
  report.value = null;
  try {
    report.value = await fetchSumsReport(
      selectedCommunity.value.ecn,
      selectedCommunity.value.ecid,
      isoRange.value.from,
      isoRange.value.to,
    );
  } catch (error) {
    $log.error('sums report failed', error);
    snackbarStore.showSnackbar(t('sumsReport.loadFailed'), '', 'Error', 15000, []);
  } finally {
    loading.value = false;
  }
}

function copyReport() {
  navigator.clipboard.writeText(reportText.value);
  snackbarStore.showSnackbar(t('sumsReport.copied'), '', 'Success', 4000, []);
}
</script>

<template>
  <v-container class="containerBase pa-0">
    <v-container class="containerStandard">
      <p class="textHeadline">{{ t('sumsReport.title') }}</p>
      <p class="mb-4">{{ t('sumsReport.description') }}</p>

      <v-combobox
        max-width="428px" bg-color="white"
        :placeholder="t('community')"
        v-model="selectedCommunity" :items="communityAvailable"
        :filter-keys="['title', 'value']" density="comfortable" variant="outlined"
        item-value="ecid" hide-details clearable class="mb-4"
      />
      <DateRangePicker
        class="mb-4"
        style="max-width: 428px"
        :dateRange="dateRange"
        :placeholder="t('dateRange')"
        @update:dateRange="(val) => dateRange = val"
      />

      <p v-if="isoRange" class="mb-4">
        {{ t('dateRange') }}: {{ isoRange.from }} — {{ isoRange.to }}
      </p>

      <PrimaryButton
        :disabled="!requestReady || loading" :loading="loading"
        @click="loadReport" :text="t('sumsReport.load')" class="mb-6"
      />

      <template v-if="report">
        <p class="font-weight-bold mt-4">{{ t('sumsReport.meterCodeSums') }}</p>
        <p v-for="s in report.meterCodeSums" :key="s.meterCode" class="text-body-2">
          {{ s.meterCode }}: {{ fmtKwh(s.energySumkWh) }} kWh ({{ s.meteringMethod }})
        </p>

        <template v-for="month in report.months" :key="month.label">
          <p class="font-weight-bold mt-6">{{ t('sumsReport.dsoSums') }} {{ month.label }}</p>
          <v-data-table density="compact" :headers="dsoHeader"
            :items="month.dsoSums" :items-per-page="25" />
        </template>

        <template v-if="report.latestAnalysis">
          <p class="font-weight-bold mt-6">{{ t('sumsReport.latestAnalysis') }}</p>
          <p class="text-body-2">
            {{ report.latestAnalysis.dateExecuted }} —
            {{ t('sumsReport.analysisRange') }}
            {{ String(report.latestAnalysis.dateStart).slice(0, 10) }} /
            {{ String(report.latestAnalysis.dateEnd).slice(0, 10) }},
            {{ t('sumsReport.clean') }}:
            {{ report.latestAnalysis.dataIsCleanOverall ? t('yes') : t('no') }}
          </p>
        </template>

        <div class="mt-6">
          <SecondaryButton @click="copyReport" :text="t('sumsReport.copyReport')" />
          <pre class="report-block mt-2">{{ reportText }}</pre>
        </div>
      </template>
    </v-container>
  </v-container>
</template>

<style scoped>
.report-block {
  background: #f5f5f5;
  padding: 12px;
  font-size: 12px;
  overflow-x: auto;
  border-radius: 4px;
}
</style>
